/**
 * 文本导出预设（纯逻辑，与 DOM 无关，便于单测）。
 *
 * 此前标题写法、是否带摘要、段落间距都写死在导出函数里，不同投稿平台
 * 各有偏好时只能改代码。这里抽成可配置预设。
 */

export interface ExportPreset {
  /** 卷标题模板；可用 {n}=卷序号（中文）、{title}=卷名 */
  volumeTitleFormat: string;
  /** 章节标题模板；可用 {n}=章节序号（中文）、{title}=章名 */
  chapterTitleFormat: string;
  /** 是否输出章节摘要 */
  includeSummary: boolean;
  /** 段落之间是否空一行（部分平台按空行分段，单换行会被连成一段） */
  blankLineBetweenParagraphs: boolean;
}

/** 默认：完整信息，含摘要 */
export const DEFAULT_PRESET: ExportPreset = {
  volumeTitleFormat: '第{n}卷 {title}',
  chapterTitleFormat: '第{n}章 {title}',
  includeSummary: true,
  blankLineBetweenParagraphs: false,
};

/** 纯净版：无摘要 + 段落空行，适合直接喂给阅读器 */
export const CLEAN_PRESET: ExportPreset = {
  ...DEFAULT_PRESET,
  includeSummary: false,
  blankLineBetweenParagraphs: true,
};

/** 投稿版：标题只保留章名（平台自行编号） */
export const SUBMISSION_PRESET: ExportPreset = {
  ...DEFAULT_PRESET,
  chapterTitleFormat: '{title}',
  includeSummary: false,
};

export const EXPORT_PRESETS: Record<string, { label: string; preset: ExportPreset }> = {
  default: { label: '默认（含摘要）', preset: DEFAULT_PRESET },
  clean: { label: '纯净版（无摘要、段落空行）', preset: CLEAN_PRESET },
  submission: { label: '投稿版（标题仅章名）', preset: SUBMISSION_PRESET },
};

/** 默认预设 id（偏好读取失败时的回落值） */
export const DEFAULT_PRESET_ID = 'default';

/** 解析预设 id：非法值回落到默认预设，避免偏好被改坏后导出异常 */
export function resolvePreset(id: string | null | undefined): ExportPreset {
  if (!id) return DEFAULT_PRESET;
  return EXPORT_PRESETS[id]?.preset ?? DEFAULT_PRESET;
}

/**
 * 套用模板。缺失的 {title} 会留下多余空格，故最后 trim。
 * 未闭合/未知占位符原样保留（不抛错），避免用户自定义模板导致导出失败。
 */
export function applyTemplate(template: string, values: { n: string; title: string }): string {
  return template
    .replace(/\{n\}/g, values.n)
    .replace(/\{title\}/g, values.title)
    .trim();
}

/** 按预设整理章节正文的段落分隔 */
export function formatBody(text: string, preset: ExportPreset): string {
  if (!preset.blankLineBetweenParagraphs) return text;
  return text.split('\n').filter((line) => line.trim().length > 0).join('\n\n');
}
